import { useState, useEffect } from 'react';
import { Mail, MailOpen, Trash2, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (contactId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.patch(`${API}/admin/contacts/${contactId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted');
      fetchContacts();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const openContact = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
    if (!contact.read) {
      markAsRead(contact.id);
    }
  };

  const filteredContacts = contacts.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div data-testid="admin-contacts-page">
        <h1 style={{ fontSize: '1.75rem', marginBottom: '2rem', fontFamily: 'Playfair Display, serif' }}>
          Messages
        </h1>

        {/* Search */}
        <div style={{ marginBottom: '1.5rem', position: 'relative', maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input"
            style={{ paddingLeft: '40px' }}
            data-testid="search-contacts-input"
          />
        </div>

        {/* Messages List */}
        {loading ? (
          <div className="loading">
            <div className="spinner" />
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <Mail size={48} style={{ color: 'var(--text-secondary)', marginBottom: '1rem', opacity: 0.5 }} />
            <p style={{ color: 'var(--text-secondary)' }}>No messages yet</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th></th>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr 
                    key={contact.id} 
                    style={{ 
                      fontWeight: contact.read ? 'normal' : 'bold',
                      backgroundColor: contact.read ? 'transparent' : 'rgba(212, 175, 55, 0.05)'
                    }}
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <td data-label="" style={{ width: '40px' }}>
                      {contact.read ? (
                        <MailOpen size={18} style={{ color: 'var(--text-secondary)' }} />
                      ) : (
                        <Mail size={18} style={{ color: 'var(--primary)' }} />
                      )}
                    </td>
                    <td data-label="From">
                      <div>{contact.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                        {contact.email}
                      </div>
                    </td>
                    <td data-label="Subject">{contact.subject}</td>
                    <td data-label="Date">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </td>
                    <td data-label="Actions">
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => openContact(contact)}
                          className="action-btn"
                          title="View"
                          data-testid={`view-contact-${contact.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => deleteContact(contact.id)}
                          className="action-btn reject"
                          title="Delete"
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {showModal && selectedContact && (
          <div className="modal-overlay" onClick={() => setShowModal(false)} data-testid="contact-detail-modal">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">{selectedContact.subject}</h2>
              
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                <p><strong>From:</strong> {selectedContact.name} ({selectedContact.email})</p>
                {selectedContact.phone && <p><strong>Phone:</strong> {selectedContact.phone}</p>}
                {selectedContact.project_type && <p><strong>Project Type:</strong> {selectedContact.project_type}</p>}
                <p><strong>Date:</strong> {new Date(selectedContact.created_at).toLocaleString()}</p>
              </div>

              <div style={{ 
                padding: '1rem', 
                backgroundColor: 'var(--background)', 
                borderRadius: '4px',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.7'
              }}>
                {selectedContact.message}
              </div>

              <div className="modal-actions">
                <a 
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                  className="btn btn-primary"
                  data-testid="reply-btn"
                >
                  Reply via Email
                </a>
                <button
                  onClick={() => deleteContact(selectedContact.id)}
                  className="btn btn-secondary"
                  style={{ borderColor: 'var(--error)', color: 'var(--error)' }}
                  data-testid="modal-delete-btn"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="btn btn-ghost"
                  data-testid="modal-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
